import React, { useMemo } from 'react';
import { Combobox, Flex, TextV2 } from '@withjoy/joykit';
import { Command } from '@withjoy/joykit/components/Command';
import type { CurrencySelectorV2Props } from './CurrencySelectorV2.types';
import type { CurrencyCodeType } from '@shared/utils/currency/currency.types';
import { useCurrencyMetadata } from './currencyMetadata';
import { formatCurrency } from './CurrencySelectorV2.utils.internal';
import { useTranslation } from '@shared/core';
import { WebGlobe } from '@withjoy/joykit/icons';
import { pxToRem } from '@withjoy/joykit/theme';
import { getCurrencySymbolFromCurrencyCode } from '@shared/utils/currency/utils';
import type { ComboboxButtonProps } from '@withjoy/joykit/components/Combobox/Combobox.types';

const CURRENCY_BUTTON_PROPS: ComboboxButtonProps = {
  intent: 'newPrimary',
  shape: 'rounded',
  variant: 'ghost',
  size: 'sm',
  overrides: {
    Root: {
      props: {
        width: pxToRem(100),
        height: pxToRem(32),
        borderRadius: pxToRem(16),
        backgroundColor: 'mono2',
        typographyVariant: 'button2',
        color: 'mono14',
        gap: pxToRem(4),
        _hover: { backgroundColor: 'mono1' },
      },
    },
  },
} as const;

const DEFAULT_CURRENCY_CODES: ReadonlyArray<CurrencyCodeType> = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY'];

export function CurrencySelectorV2<C extends CurrencyCodeType = CurrencyCodeType>({
  value,
  onChange,
  disabled,
  placeholder,
  searchable = true,
  mode,
  currencies,
  hideFlag = false,
}: CurrencySelectorV2Props<C>) {
  const { tq } = useTranslation('currencySelectorV2');

  // Collect all unique codes that need metadata resolution
  const allCodes = useMemo((): ReadonlyArray<CurrencyCodeType> => {
    if (!currencies) return DEFAULT_CURRENCY_CODES;

    const popular = currencies.popularCurrencyCodes ?? [];
    const all = currencies.allCurrencyCodes ?? [];
    const popularSet = new Set<CurrencyCodeType>(popular);
    const deduplicatedAll = all.filter(c => !popularSet.has(c));
    return [...popular, ...deduplicatedAll] as ReadonlyArray<CurrencyCodeType>;
  }, [currencies]);

  const resolvedCurrencies = useCurrencyMetadata(allCodes);

  const isButtonMode = mode === 'button';

  const formatOptions = useMemo(() => ({ hideFlag }), [hideFlag]);

  const displayValue = useMemo(() => {
    if (!value) return '';
    const currency = resolvedCurrencies.find(c => c.code === value);
    if (!currency) return '';
    if (isButtonMode) {
      const symbol = getCurrencySymbolFromCurrencyCode(currency.code);
      return `${symbol} ${currency.code}`;
    }
    const formatted = formatCurrency(currency.code, currency.name, currency.flag, formatOptions);
    return formatted.inputDisplay;
  }, [value, resolvedCurrencies, isButtonMode, formatOptions]);

  const currencyItems = useMemo(() => {
    return resolvedCurrencies.map(currency => {
      const formatted = formatCurrency(currency.code, currency.name, currency.flag, formatOptions);
      return (
        <Command.Item
          key={currency.code}
          value={currency.code}
          keywords={[currency.name, currency.code, formatted.symbol]}
        >
          <Flex
            flexDirection="column"
            gap={0}
          >
            <TextV2 typographyVariant={isButtonMode ? 'button1' : 'body1'}>{formatted.popoverDisplayLine1}</TextV2>
            <TextV2
              typographyVariant={isButtonMode ? 'label1' : 'body2'}
              color={isButtonMode ? 'mono10' : 'mono7'}
              marginTop={isButtonMode ? { _: 0, sm2: 1 } : 0}
            >
              {formatted.popoverDisplayLine2}
            </TextV2>
          </Flex>
        </Command.Item>
      );
    });
  }, [resolvedCurrencies, isButtonMode, formatOptions]);

  const handleValueChange = (currencyCode: string) => {
    onChange(currencyCode as C);
  };

  const comboboxProps = {
    value,
    onValueChange: handleValueChange,
    disabled,
    placeholder: placeholder || tq`Select currency...`,
    searchPlaceholder: tq`Search currencies...`,
    emptyMessage: tq`No currencies found`,
    displayValue,
    searchable,
  };

  if (isButtonMode) {
    return (
      <Combobox
        mode="button"
        buttonProps={CURRENCY_BUTTON_PROPS}
        buttonModeMobileIcon={
          <WebGlobe
            size={24}
            color="mono14"
          />
        }
        dialogTitle={tq`Choose Currency`}
        {...comboboxProps}
      >
        {currencyItems}
      </Combobox>
    );
  }

  return <Combobox {...comboboxProps}>{currencyItems}</Combobox>;
}

CurrencySelectorV2.displayName = 'CurrencySelectorV2';
